import { useState, useEffect } from "react";
import ImageUpload from "../../components/ImageUpload/ImageUpload";
import { IApp } from "../../types";
import { APP_CATEGORIES } from "../../../utils/constants";

interface AppFormProps {
  app: IApp | null;
  onSubmit: (formData: FormData) => void;
  onCancel: () => void;
}

const AppForm = ({ app, onSubmit, onCancel }: AppFormProps) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    link: "",
    category: "",
    featured: false,
    published: false,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (app) {
      setFormData({
        name: app.name,
        description: app.description,
        link: app.link,
        category: app.category,
        featured: app.featured || false,
        published: app.published || false,
      });
      setImagePreview(app.image);
    }
  }, [app]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    data.append("name", formData.name);
    data.append("description", formData.description);
    data.append("link", formData.link);
    data.append("category", formData.category);
    data.append("featured", String(formData.featured));
    data.append("published", String(formData.published));

    if (imageFile) {
      data.append("image", imageFile);
    }

    await onSubmit(data);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="admin-form">
      <ImageUpload
        value={imagePreview}
        onChange={setImageFile}
        label="App Image"
      />

      <div className="form-group">
        <label>App Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Enter app name"
        />
      </div>

      <div className="form-group">
        <label>Description *</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={4}
          placeholder="Enter app description"
        />
      </div>

      <div className="form-group">
        <label>App Link *</label>
        <input
          type="url"
          name="link"
          value={formData.link}
          onChange={handleChange}
          required
          placeholder="https://example.com"
        />
      </div>

      <div className="form-group">
        <label>Category *</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
        >
          <option value="">Select category</option>
          {APP_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group-inline">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="featured"
            checked={formData.featured}
            onChange={handleChange}
          />
          <span>Featured App</span>
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            name="published"
            checked={formData.published}
            onChange={handleChange}
          />
          <span>Published</span>
        </label>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "Saving..." : app ? "Update App" : "Create App"}
        </button>
      </div>
    </form>
  );
};

export default AppForm;
